import { BrowserObjectModel } from '../components/browser-object-model';
import { Injectable, MultiInject, Optional } from '../components/dependency-injection/decorators';
import { AdSlotContainerItemViewEvent } from '../components/event-manager/event/ad-slot-container-item-view.event';
import { Events } from '../components/event-manager/event/events';
import { EventManager } from '../components/event-manager/event-manager';
import { Logger } from '../components/logger/logger';
import { StylesheetRenderer } from '../components/renderer/stylesheet.renderer';
import { ViewObserver } from '../components/renderer/utils/view-observer';
import { AdSlotContainerItem } from '../entities/ad-slot-container-item';
import { CreativeViewInterface } from '../interfaces/containers/creative-view.interface';

@Injectable()
export class AdSlotContainerItemView {
  private static readonly DEFAULT_REFERRER_POLICY = 'strict-origin-when-cross-origin, no-referrer-when-downgrade';

  public constructor (
    private readonly browserObjectModel: BrowserObjectModel,
    private readonly eventManager: EventManager,
    private readonly stylesheetRenderer: StylesheetRenderer,
    @MultiInject('AdSlotContainerItemViewCreative') private readonly creativeViews: CreativeViewInterface[],
    @Optional() private readonly logger?: Logger,
  ) {
  }

  public async render (item: AdSlotContainerItem, viewObserver?: ViewObserver): Promise<Element> {
    const adSlot = item.getAdSlotContainer()?.getAdSlot();

    if (!adSlot) {
      throw new Error('AdSlot is required to render AdSlotContainer');
    }

    const listItemElement = this.browserObjectModel.getDocument().createElement('li');
    listItemElement.className = this.stylesheetRenderer.getClass(adSlot, 'listItem');

    const innerElement = this.creativeViews.find((creativeView) => creativeView.supports(item));

    if (!innerElement) {
      this.logger?.warn('No creative view found for item', item);
      return listItemElement;
    }

    listItemElement.innerHTML = innerElement.render(item).innerHTML;

    (viewObserver ?? ViewObserver.getInstance({ trackVisibility: false, delay: 100 }))
      .observe(listItemElement, {
        freezeOnceVisible: true,
        minimumVisibleTime: adSlot?.getSettings().getItemImpressionTrackTimeout() ?? 0,
        threshold: adSlot?.getSettings().getItemImpressionMinimumVisibleThreshold() ?? 0.25,
        callback: async (element, isInView) => {
          if (!isInView) {
            return;
          }

          await this.eventManager.dispatch(
            Events.AD_SLOT_CONTAINER_ITEM_VIEW,
            new AdSlotContainerItemViewEvent(item, element),
          );

          this.logger?.debug('Item view event dispatched', {
            item,
            adSlot: adSlot.getId(),
          });
        },
      });

    return listItemElement;
  }
}
